<script lang="ts" setup>
import type { BreadcrumbItem } from "~~/models/breadcrumb";
import type { CategoryTree } from "~~/models/categories";
import type { PlanktonCategoryWithSlugs } from "~~/server/plankton";
import { withQuery } from "ufo";
import { z } from "zod";

const {
  viewMode,
  categoryTree,
  category,
} = defineProps<{
  viewMode: "list" | "details";
  categoryTree: CategoryTree;
  category?: PlanktonCategoryWithSlugs | null | undefined;
}>();

const { t } = useI18n();
const { path, query } = useRoute();

const querySchema = z.object({
  term: z.string().optional(),
});

const { success: validQuerySuccess, data: validQueryData } = querySchema.safeParse(query);

const breadcrumbs = ref([] as Array<BreadcrumbItem>);

let currentCategory = categoryTree.getCategory(category?.id ?? 0);

while (currentCategory) {
  const prefix = path.includes("/happy-deals") ? "/happy-deals" : "/catalog";
  let link = `${prefix}${categoryTree.getPath(currentCategory.id)}`;

  if (validQuerySuccess && validQueryData?.term) {
    link = withQuery(link, { term: validQueryData.term });
  }

  breadcrumbs.value.unshift({ title: categoryTree.getName(currentCategory.id), link: link });

  currentCategory = categoryTree.getCategory(currentCategory.parentId);
}

if (validQuerySuccess && validQueryData?.term) {
  const link = `/catalog?term=${validQueryData.term}`;
  breadcrumbs.value.unshift({ title: `${t("catalog.search-by")} '${validQueryData.term}'`, link: link });
}

breadcrumbs.value.unshift({ title: t("breadcrumb.home"), link: path.includes("/happy-deals") ? "/happy-deals" : "/" });

const backPosition = viewMode === "list" ? 2 : 1;
const backLink = breadcrumbs.value.at(breadcrumbs.value.length - backPosition)?.link;

if (viewMode === "list") {
  const unlinkItem = breadcrumbs.value.at(breadcrumbs.value.length - 1);

  if (unlinkItem) {
    unlinkItem.link = undefined;
  }
}
</script>

<template>
  <nav v-if="breadcrumbs.length > 1" v-bind="$attrs" class="flex text-xs font-medium leading-5" aria-label="Breadcrumbs">
    <ul v-if="backLink" class="pe-md me-md block border-e border-neutral-100">
      <NuxtLinkLocale :to="backLink" class="gap-xs flex items-center">
        <UIcon name="i-ph-caret-left" class="text-primary" />
        <span>{{ t("breadcrumb.back") }}</span>
      </NuxtLinkLocale>
    </ul>

    <ul class="flex flex-wrap">
      <Breadcrumb
        v-for="(item, idx) in breadcrumbs" :key="item.link" :item="item"
        :class="{
          'font-bold': item.title === t('breadcrumb.home'),
          'underline': item.title !== t('breadcrumb.home') && item.link,
          'hidden md:block': idx > 0 && idx < breadcrumbs.length - 1,
        }"
      />
    </ul>

    
  </nav>
</template>

<style scoped>

</style>
